import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Box, Divider, Grid, Text, useMantineColorScheme, useMantineTheme } from "@mantine/core";
import { Question, Questionnaire, QuestionOption } from "src/graphql/generated/graphql";
import { checkQuestionType } from "src/utils/helpers";

import { FormItem } from "../FormItem";

export interface QuestionnairePreviewProps {
    questionnaire: Questionnaire;
}

export const QuestionnairePreview = ({ questionnaire }: QuestionnairePreviewProps) => {
    const { t } = useTranslation(["common"]);

    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();

    const renderOption = (option: QuestionOption, index: number) => {
        return <Box key={`${index}${option?.key}`}>- {option?.text}</Box>;
    };

    const renderQuestion = (question: Question, index: number) => {
        return (
            <Fragment key={`${index}${question?.key}`}>
                {index !== 0 && <Divider color={colorScheme === "dark" ? "gray.9" : "gray.1"} />}
                <Box py="md">
                    <Text weight={500} display="flex" mb="md" style={{ alignItems: "center" }}>
                        <Box
                            display="inline-block"
                            pos="relative"
                            w="2em"
                            h="2em"
                            mr="md"
                            style={{
                                border: `1px solid ${
                                    theme.colorScheme === "dark" ? theme.colors.gray[8] : theme.colors.gray[4]
                                }`,
                                borderRadius: "50%",
                            }}
                        >
                            <Box
                                display="inline-block"
                                pos="absolute"
                                top="50%"
                                left="50%"
                                style={{ transform: "translate(-40%,-50%)" }}
                            >
                                {index + 1}.
                            </Box>
                        </Box>{" "}
                        {question?.text}
                    </Text>
                    <Grid>
                        <Grid.Col xs={12} sm={6}>
                            <Grid>
                                <Grid.Col xs={12} sm={6}>
                                    <FormItem label={t("questionnaire_question_isoptional")}>
                                        {question?.isOptional ? t("yes") : t("no")}
                                    </FormItem>
                                </Grid.Col>
                                <Grid.Col xs={12} sm={6}>
                                    <FormItem label={t("questionnaire_question_type")}>
                                        {checkQuestionType(question?.type)
                                            ? t(`questionnaire_question_type_${question?.type}`)
                                            : question?.type}
                                    </FormItem>
                                </Grid.Col>
                            </Grid>
                        </Grid.Col>
                        <Grid.Col xs={12} sm={6}>
                            {question?.options?.length > 0 && (
                                <FormItem label={t("questionnaire_question_options")}>
                                    {(question?.options || []).map(renderOption)}
                                </FormItem>
                            )}
                        </Grid.Col>
                    </Grid>
                </Box>
            </Fragment>
        );
    };

    return <>{(questionnaire?.questions || []).map(renderQuestion)}</>;
};
